import { useEffect, useMemo, useState } from 'react'
import { Alert, Box, IconButton, MenuItem, TextField, Typography } from '@mui/material'
import { CircleAlert, CircleCheck, Clock, X } from 'lucide-react'
import { AppShell, Crumb, Flag as CountryFlag, tokens, useNavigate } from '@agribot/polygon-checks-ui'
import { OriginMap, countryName, type Origin } from '@agribot/sourcing-model'
import { useCampaignOrigins, useCampaigns, useParentCompanies, useTaxonomy, type CampaignOrigin } from '../api'
import { useSupplyGeojson } from '../hooks'
import { SupplyDrill, SupplySummaryLine } from '../components/SupplyDrill'
import { STATUS_LABEL } from '../rules'

const t = (v: number) => `${v.toLocaleString('en-GB', { maximumFractionDigits: v < 10 ? 1 : 0 })} t`

/**
 * Screen 12: every origin in a campaign on one map, coloured by its check,
 * filterable by commodity, group, submission status and check result.
 * Choosing one shows which legal entities and materials use it and its
 * share of each, its farms, and the way into each review.
 */
export function OriginsMap() {
  const { data: campaigns } = useCampaigns()
  const { data: taxonomy } = useTaxonomy()
  const { data: parents } = useParentCompanies()
  const navigate = useNavigate()
  const [campaignId, setCampaignId] = useState<string>('')
  useEffect(() => {
    if (!campaignId && campaigns?.length) setCampaignId((campaigns.find((c) => c.status === 'open') ?? campaigns[0]!).id)
  }, [campaigns, campaignId])
  const [filter, setFilter] = useState({ commodityId: '', parentCompanyId: '', status: '', check: '' })
  const { data: origins, error, isFetching } = useCampaignOrigins(campaignId || undefined, filter)
  const [selected, setSelected] = useState<string | null>(null)
  const chosen = origins?.find((o) => o.id === selected) ?? null
  const { data: plots } = useSupplyGeojson(chosen?.supply ? chosen.id : null)
  const [farms, setFarms] = useState(false)
  const asOrigins = useMemo(() => (origins ?? []).map((o) => ({ ...o, entityIds: [] }) as Origin), [origins])
  const counts = useMemo(() => ({
    total: origins?.length ?? 0,
    failed: origins?.filter((o) => o.check.state === 'failed').length ?? 0,
    farms: origins?.filter((o) => o.supply).length ?? 0,
  }), [origins])

  const select = (label: string, key: keyof typeof filter, options: { value: string; label: string }[]) => (
    <TextField select size="small" label={label} value={filter[key]} onChange={(e) => setFilter((f) => ({ ...f, [key]: e.target.value }))}
      InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={{ minWidth: 170 }}>
      <MenuItem value="">All</MenuItem>
      {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
    </TextField>
  )

  return (
    <AppShell crumbs={[<Crumb key="a">Sourcing</Crumb>, <Crumb key="b">Origins map</Crumb>]}>
      <Box sx={{ flex: 'none', px: 6, py: 3, display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', borderBottom: `1px solid ${tokens.color.ink200}`, backgroundColor: 'background.paper' }}>
        <TextField select size="small" label="Campaign" value={campaignId} onChange={(e) => { setCampaignId(e.target.value); setSelected(null) }} InputLabelProps={{ shrink: true }} sx={{ minWidth: 260 }}>
          {(campaigns ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </TextField>
        {select('Commodity', 'commodityId', (taxonomy?.commodities ?? []).filter((c) => c.active).map((c) => ({ value: c.id, label: c.name })))}
        {select('Group', 'parentCompanyId', (parents ?? []).filter((p) => !p.parentId).map((p) => ({ value: p.id, label: p.name })))}
        {select('Submission', 'status', Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label })))}
        {select('Check', 'check', [{ value: 'passed', label: 'Passed' }, { value: 'failed', label: 'Failed' }, { value: 'pending', label: 'Not checked yet' }])}
        <Typography variant="body2" sx={{ color: 'text.secondary', ml: 'auto' }}>
          {isFetching ? 'Loading…' : `${counts.total} origins · ${counts.failed} failed a check · ${counts.farms} with farms linked`}
        </Typography>
      </Box>
      {error && <Alert severity="error" sx={{ m: 4 }}>{(error as Error).message}</Alert>}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <OriginMap origins={asOrigins} labels={{ region: 'Map of the campaign\'s origins' }} height="fill" padding={64} showReadout selected={selected} onSelect={(id) => { setSelected(id); setFarms(false) }} fitKey={`${campaignId}|${JSON.stringify(filter)}|${(origins ?? []).map((o) => o.id).join(',')}`} plots={plots ?? null} />
        {chosen && (
          <Box sx={{ width: 460, flex: 'none', overflow: 'auto', borderLeft: `1px solid ${tokens.color.ink200}`, backgroundColor: 'background.paper', p: 5 }}>
            <OriginPanel origin={chosen} farms={farms} onFarms={() => setFarms((v) => !v)} onClose={() => setSelected(null)} onOpen={(id) => navigate(`/sourcing/reviews/${id}`)} />
          </Box>
        )}
      </Box>
    </AppShell>
  )
}

function OriginPanel({ origin, farms, onFarms, onClose, onOpen }: {
  origin: CampaignOrigin
  farms: boolean
  onFarms: () => void
  onClose: () => void
  onOpen: (submissionId: string) => void
}) {
  const check = origin.check
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }}>
        <Typography variant="h3" component="h2" sx={{ flex: 1 }}>{origin.name}</Typography>
        <IconButton aria-label="Close" size="small" onClick={onClose}><X size={16} /></IconButton>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.secondary', font: `400 13px ${tokens.font.sans}` }}>
        <CountryFlag code={origin.countryCode} size={12} />{countryName(origin.countryCode, 'en')}
        {origin.parentCompany && <> · {origin.parentCompany.name}</>} · {origin.kind === 'area' ? 'Area' : origin.kind === 'farms' ? 'Made of farms' : 'Point'}
      </Box>
      <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start', font: `500 13px ${tokens.font.sans}`, color: check.state === 'failed' ? tokens.color.danger : check.state === 'passed' ? tokens.color.success : 'text.secondary' }}>
        <Box sx={{ flex: 'none', mt: '1px' }}>{check.state === 'failed' ? <CircleAlert size={14} /> : check.state === 'passed' ? <CircleCheck size={14} /> : <Clock size={14} />}</Box>
        {check.state === 'failed' ? check.reason : check.state === 'passed' ? 'Checks passed' : 'Not checked yet'}
      </Box>
      {origin.supply && (
        <Box sx={{ mt: 2 }}>
          <SupplySummaryLine supply={origin.supply} open={farms} onToggle={onFarms} />
          {farms && <SupplyDrill originId={origin.id} />}
        </Box>
      )}
      {!origin.supply && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>No farms linked.</Typography>}
      <Typography variant="overline" component="h3" sx={{ color: 'text.disabled', mt: 5, mb: 1 }}>Supplies · {t(origin.uses.reduce((n, u) => n + u.tonnes, 0))} a year</Typography>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {origin.uses.map((u, i) => (
          <Box component="li" key={`${u.submissionId}-${u.materialId}-${u.ingredientId}-${i}`} sx={{ py: 2, borderTop: `1px solid ${tokens.color.ink200}` }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline' }}>
              <Box sx={{ flex: 1, font: `500 14px ${tokens.font.sans}` }}>{u.supplier}</Box>
              <Box sx={{ font: `600 13px ${tokens.font.sans}`, fontVariantNumeric: 'tabular-nums' }}>{t(u.tonnes)}</Box>
            </Box>
            <Box sx={{ font: `400 12px ${tokens.font.sans}`, color: 'text.secondary' }}>
              {u.material} · {u.ingredient} ({u.commodity}) · {u.volumeSharePct.toLocaleString('en-GB', { maximumFractionDigits: 1 })}% of the part
            </Box>
            <Box component="button" type="button" onClick={() => onOpen(u.submissionId)}
              sx={{ mt: 0.5, border: 0, background: 'none', p: 0, cursor: 'pointer', color: tokens.color.brandTeal, font: `500 12px ${tokens.font.sans}` }}>
              {(STATUS_LABEL as Record<string, string>)[u.submissionStatus] ?? u.submissionStatus} · open the review
            </Box>
          </Box>
        ))}
      </Box>
    </>
  )
}
